import { DoohAgeVariables, DoohGenderVariables } from 'sonata-static-storage/lib/enum/doohAudienceVariable.enum';

import type { TagItem } from '@/components/common/FormExpandableSection/FormExpandableSection';
import type { CoverageVariableNode, DoohAudience } from '@/services/doohAudience.service';

export type DoohGender = 'all' | keyof typeof DoohGenderVariables;
export type SocialMediaOption = { id: string; label: string };
type TagLabels = {
  ages: (count: number) => string;
  ai: string;
  female: string;
  male: string;
  socialMedia: (count: number) => string;
  totalPopulation: string;
};

const SOCIAL_MEDIA_PREFIX = 'SM_';
const AGE_VARIABLES: string[] = Object.values(DoohAgeVariables);
const GENDER_VARIABLES: string[] = Object.values(DoohGenderVariables);

export const getDoohGender = (variables: string[]): DoohGender => {
  const genders = variables.filter(variable => GENDER_VARIABLES.includes(variable));
  if (genders.length !== 1) {
    return 'all';
  }
  return genders[0] === DoohGenderVariables.f ? 'f' : 'm';
};

export const getDoohAgeVariables = (variables: string[]): string[] =>
  AGE_VARIABLES.filter(variable => variables.includes(variable));

export const getSocialMediaVariables = (variables: string[]): string[] =>
  variables.filter(variable => variable.startsWith(SOCIAL_MEDIA_PREFIX));

/** The variables of the selection: an empty gender or age selection means all */
export const buildDoohVariables = (gender: DoohGender, ages: string[], socialMedia: string[]): string[] => [
  ...(gender === 'all' ? [] : [DoohGenderVariables[gender]]),
  ...AGE_VARIABLES.filter(variable => ages.includes(variable)),
  ...socialMedia
];

/** The social media variables (SM_*) of the coverage catalogue, by name */
export const getSocialMediaOptions = (catalogue: Record<string, CoverageVariableNode>): SocialMediaOption[] => {
  const collect = (nodes: Record<string, CoverageVariableNode>): SocialMediaOption[] =>
    Object.entries(nodes).flatMap(([id, node]) =>
      node.items ? collect(node.items) : id.startsWith(SOCIAL_MEDIA_PREFIX) ? [{ id, label: node.description }] : []
    );
  return collect(catalogue).sort((a, b) => a.label.localeCompare(b.label));
};

/** None while disabled; with a text, only that the AI generates it: the variables of the AI are never shown */
export const getDoohDemographyTags = (
  { enableGenerateReport, text, variables }: DoohAudience,
  labels: TagLabels
): TagItem[] => {
  if (!enableGenerateReport) {
    return [];
  }
  if (text?.trim()) {
    return [{ label: labels.ai }];
  }
  if (!variables.length) {
    return [{ label: labels.totalPopulation }];
  }
  const gender = getDoohGender(variables);
  const ages = getDoohAgeVariables(variables).length;
  const socialMedia = getSocialMediaVariables(variables).length;
  return [
    ...(gender === 'all' ? [] : [{ label: gender === 'f' ? labels.female : labels.male }]),
    ...(ages ? [{ label: labels.ages(ages) }] : []),
    ...(socialMedia ? [{ label: labels.socialMedia(socialMedia) }] : [])
  ];
};
